import React, { useMemo, useState } from "react";
import { ArchiveOutlined, Close, SaveAsOutlined, SaveOutlined, UnarchiveOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { WorkflowDefinitionGraphDto } from "../../types/workflowDefinitionDto";
import { WorkflowSettingsValues } from "./useWorkflowBuilderGraph";
import { useActiveUsers, WorkflowActiveUser } from "./useActiveUsers";
import { useWorkflowUserSearch } from "./workflowUserSearch";

interface WorkflowSettingsDialogProps {
  open: boolean;
  /** Null for a workflow that has never been saved — Archive / Save as new version need one. */
  definition: WorkflowDefinitionGraphDto | null;
  /** The title as currently typed in the top bar, which may be ahead of the saved name. */
  title: string;
  busy: boolean;
  onClose: () => void;
  onSave: (values: WorkflowSettingsValues) => void;
  onToggleArchive: () => void;
  onSaveAsNewVersion: () => void;
}

const URL_PATTERN = /^https?:\/\/\S+$/i;
const URL_ERROR = "Enter a full URL starting with https://";

const blankToNull = (value: string) => (value.trim() ? value.trim() : null);

/** The top-bar wrench: workflow-level settings, Archive / Un-archive, Save as new version. */
const WorkflowSettingsDialog: React.FC<WorkflowSettingsDialogProps> = ({
  open,
  definition,
  title,
  busy,
  onClose,
  onSave,
  onToggleArchive,
  onSaveAsNewVersion,
}) => {
  // Seeded once per mount. The page remounts this dialog each time it opens (via `key`), so a
  // cancelled edit never lingers into the next opening.
  const [name, setName] = useState(title);
  const [description, setDescription] = useState(definition?.description ?? "");
  const [category, setCategory] = useState(definition?.category ?? "");
  const [smeUserId, setSmeUserId] = useState<number | null>(definition?.smeUserId ?? null);
  const [redirectUrl, setRedirectUrl] = useState(definition?.redirectUrl ?? "");
  const { users, loading: usersLoading } = useActiveUsers(open);

  const selectedSme = useMemo(
    () =>
      users.find((user) => user.userId === smeUserId) ??
      (smeUserId ? { userId: smeUserId, userName: `User #${smeUserId}` } : null),
    [users, smeUserId],
  );
  const smeSearch = useWorkflowUserSearch(users);

  const nameMissing = !name.trim();
  const urlInvalid = Boolean(redirectUrl.trim()) && !URL_PATTERN.test(redirectUrl.trim());
  const archived = definition?.status === "archived";

  const handleSave = () => {
    if (nameMissing || urlInvalid) return;
    onSave({
      name: name.trim(),
      description: blankToNull(description),
      category: blankToNull(category),
      smeUserId,
      redirectUrl: blankToNull(redirectUrl),
    });
  };

  const handleSmeChange = (_event: React.SyntheticEvent, option: WorkflowActiveUser | null) =>
    setSmeUserId(option?.userId ?? null);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="workflow-settings-title">
      <DialogTitle id="workflow-settings-title">Workflow settings</DialogTitle>
      <IconButton aria-label="close" onClick={onClose} className="dialog-close-button">
        <Close />
      </IconButton>
      <DialogContent className="workflow-builder__settings-dialog" dividers>
        <TextField
          label="Workflow name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={nameMissing}
          helperText={nameMissing ? "A workflow needs a name." : undefined}
          size="small"
          fullWidth
          required
        />
        <TextField
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          size="small"
          multiline
          minRows={3}
          fullWidth
        />
        <Box className="workflow-builder__settings-row">
          <TextField
            label="Category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            size="small"
            fullWidth
          />
          <Autocomplete
            fullWidth
            size="small"
            options={users}
            loading={usersLoading}
            value={selectedSme}
            onChange={handleSmeChange}
            getOptionLabel={(option) => option.userName}
            {...smeSearch}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subject-matter expert"
                placeholder="Who to ask about this workflow"
              />
            )}
          />
        </Box>
        <TextField
          label="Archived redirect URL"
          placeholder="https://"
          value={redirectUrl}
          onChange={(event) => setRedirectUrl(event.target.value)}
          error={urlInvalid}
          size="small"
          fullWidth
          helperText={
            urlInvalid
              ? URL_ERROR
              : "Used only when this workflow is archived, so bookmarked links land on the current version."
          }
        />
      </DialogContent>
      <DialogActions className="workflow-builder__settings-actions">
        <Button
          variant="outlined"
          size="small"
          startIcon={archived ? <UnarchiveOutlined /> : <ArchiveOutlined />}
          onClick={onToggleArchive}
          disabled={!definition || busy}
        >
          {archived ? "Un-archive" : "Archive"}
        </Button>
        <Button
          className="workflow-builder__settings-version"
          variant="outlined"
          size="small"
          startIcon={<SaveAsOutlined />}
          onClick={onSaveAsNewVersion}
          disabled={!definition || busy}
        >
          Save as new version
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
          onClick={handleSave}
          disabled={nameMissing || urlInvalid || busy}
        >
          Save settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowSettingsDialog;
